/* Core & libraries */
import React, {useEffect, useState} from "react";
import { useTheme } from "react-jss";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import {FormattedMessage, defineMessages, useIntl} from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from "../../hooks/useHasPermission";
import { useGlobalContext, useGlobalUpdateContext } from "../../contexts/globalContext";
import { useAuthorizedUsersService } from '../../services/authorizedUsersService';
import { useIsMobile } from "../../hooks/useIsMobile";
import { AIRLINES } from "../../constants/appConstants";
import { useUserContext } from "../../contexts/userContext";
import { formatTimeInterval } from "../../helpers/general.js";

/* Components */
import UCInput from "../ucinput";
import UCButton from "../buttons";
import { ReactComponent as SearchIcon } from '../../resources/images/search.svg';
import { ReactComponent as SortMenuIcon } from '../../resources/images/sort-menu.svg';
import { ReactComponent as SendIcon } from '../../resources/images/send-01.svg';
import { ReactComponent as DeleteIcon } from '../../resources/images/trash-01.svg';
import { ReactComponent as CheckSortIcon } from "../../resources/images/check-sort-icon.svg";
import { ReactComponent as MailIcon } from "../../resources/images/mail-01.svg";
import AuthorisedCardUsersNotice from "../AuthorisedCardUsersNotice/index";
import Spinner from "../../components/spinners/buttonSpinner";
import UCCheckbox from '../uccheckbox';


/* Styles */
import styles from "./styles.js";


const messages = defineMessages({
  searchAreaMessage: {
    id: 'pendinginvitations.search'
  }
})

const Content = (props) => {
  const { pendingInviteResponse, pendingInvitations, setPending, revokeInvite, resendInvite, isFetching, showPendingInvitationsModal, invitationCount } = props;

  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();
  const isMobile = useIsMobile();

  const activePage = pendingInviteResponse?.results?.pageNumber;
  const pageCount = pendingInviteResponse?.results?.pagesAvailable;
  const filterTotal = pendingInviteResponse?.results?.filterTotal;

  const [query, setQuery] = useState("");
  const [firstSearch, setFirstSearch] = useState(true);

  const [sortType, setSortType] = useState("ascInviteDate")

  const [data, setData] = useState([])

  const { checkPermission } = useHasPermission(); 

  const globalProperties  = useGlobalContext();
  const setGlobalProperties  = useGlobalUpdateContext();
  const { shellUserCount , getShellUserCount } = useAuthorizedUsersService();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [invitationsCheckboxIds, setInvitationsCheckboxIds] = useState([]);
  const [allInvitesSelectedBeforeSearch, setAllInvitesSelectedBeforeSearch] = useState(false)
  const [selectAllActive, setSelectAllActive] = useState(false)

  const allInvitationsSelected = invitationsCheckboxIds.length === data.length

  useEffect(() => {
    getShellUserCount();
  }, [shellUserCount])

  useEffect(() => {
    if (firstSearch) {
      setFirstSearch(false)
    } else {
      const timeOutId = setTimeout(() => {
        if (data.length !== 0 && invitationsCheckboxIds.length === data.length) setAllInvitesSelectedBeforeSearch(true)
        if (query === "" || query.length > 2) {
          setPending({
            ...pendingInvitations,
            searchterm: query,
            page: 1
          })
        }
      }, 500);
      return () => clearTimeout(timeOutId);
    }
  }, [query]);


  useEffect(() => {
    if ( activePage === 1){
      setData(pendingInviteResponse?.results?.dataItems)
    } else {
      setData( data =>[ ...data, ...pendingInviteResponse?.results?.dataItems])
    }
  }, [pendingInviteResponse?.results?.dataItems]);

  useEffect(() => {
    if (pendingInvitations?.page !== 1) {
      if (selectAllActive) {
        setInvitationsCheckboxIds( prevData =>[ ...prevData, ...pendingInviteResponse?.results?.dataItems] )
      }
    }
  }, [pendingInvitations?.page])

  useEffect(() => {
    if(allInvitesSelectedBeforeSearch && pendingInvitations?.searchterm?.length > 2) {
      setInvitationsCheckboxIds(data)
      setAllInvitesSelectedBeforeSearch(false)
    }

    // used for auto selecting visible pendings when changing the sort selection
    if (allInvitationsSelected) {
      setInvitationsCheckboxIds(data)
    }
  }, [data])

  useEffect(() => {
    if (!showPendingInvitationsModal) {
      setInvitationsCheckboxIds([])
    }
  }, [showPendingInvitationsModal])

  const handleDateAscSort = () =>{
    setSortType("ascInviteDate")
    setPending({
      ...pendingInvitations,
      page:1,
      sortby: "invitationDateTime",
      sortdirection: "dsc",
    })
    document.getElementById("root").click();
  }
  const handleDateDescSort = () =>{
    setSortType("descInviteDate")
    setPending({
      ...pendingInvitations,
      page:1,
      sortby: "invitationDateTime",
      sortdirection: "asc",
    })
    document.getElementById("root").click();
  }
  const handleEmailAscSort = () =>{
    setSortType("ascEmail")
    setPending({
      ...pendingInvitations,
      page:1,
      sortby: "email",
      sortdirection: "asc",
    })
    document.getElementById("root").click();
  }

  const handleNextPage = (page) => {
    if ( activePage !== pageCount && !isFetching ) {
      setPending({
        ...pendingInvitations,
        page: page,
      })
    }
  }

  const renderSort = () => {
    return(
      <Popover className={classes.popover}>
        <div className={classes.popoverDiv}>
          <div className={classes.popoverTitle}>
            <FormattedMessage id="pendinginvitations.sort" />
          </div>
          <div className={classes.buttonsStyle}>
            <div onClick={handleDateAscSort} className={classes.sortSelection}>
              <FormattedMessage id="pendinginvitations.sortDateAsc" />
              {sortType === "ascInviteDate" && <CheckSortIcon/>  }
            </div>
            <div onClick={handleDateDescSort} className={classes.sortSelection}>
              <FormattedMessage id="pendinginvitations.sortDateDesc" />
              {sortType === "descInviteDate" && <CheckSortIcon/>  }
            </div>
            <div onClick={handleEmailAscSort} className={classes.sortSelection}>
              <FormattedMessage id="pendinginvitations.sortEmailAsc" />
              {sortType === "ascEmail" && <CheckSortIcon/>  }
            </div>
          </div>
        </div>
      </Popover>
    )
  }

  const handleResend = async (e, invitation) => {
    const resendResponse = await resendInvite({
      invitationIds: [invitation.invitationID]
    }, true)
    if (resendResponse) {
      setPending({
        ...pendingInvitations,
        page:1,
      })
    }
  };

  const handleDelete = async (e, invitation) => {
    const deleteResponse = await revokeInvite(invitation)
    if (deleteResponse) {
      setPending({
        ...pendingInvitations,
        page:1,
      })
    }

    //remove invite from selected arr when it is selected and individually removed
    if (invitationsCheckboxIds.some(inv => inv.invitationID === invitation.invitationID) ) {
      let newArr = []
      newArr = invitationsCheckboxIds.filter(invite => invite.invitationID !== invitation.invitationID);
      setInvitationsCheckboxIds(newArr)
    }
  };

  const handleCancelResend = () => {
    setInvitationsCheckboxIds([])
    setSelectAllActive(false)
  };

  const handleMultipleResend = async () => {
    const reqBody = selectAllActive && query.length <= 2 ? {
      allInvitations: allInvitationsSelected,
      ...(showExpired ? { status: 'EXPIRED' } : {}),
    } : {
      invitationIds: invitationsCheckboxIds.map(invite => invite.invitationID),
      ...(showExpired ? { status: 'EXPIRED' } : {}),
    }

    const resendResponse = await resendInvite(reqBody, false, filterTotal)
    if (resendResponse) {
      const wasExpired = showExpired;
      if (wasExpired) setShowExpired(false);
      setPending({
        ...pendingInvitations,
        page: 1,
        status: undefined,
      })
      setInvitationsCheckboxIds([])
      setSelectAllActive(false)
    }
  }

  const handleScroll = event => {
    const container = event.currentTarget;
    const scrollHeight = container.scrollHeight;
    const scrollTop = container.scrollTop;
    const height = container.offsetHeight;

    const scrollPercentage = (scrollTop + height) / scrollHeight;

    if (scrollPercentage > 0.8 && !isFetching) {
      handleNextPage(activePage < pageCount ? activePage + 1 : pageCount)
    }
  };


  const handleInviteNow = async () =>{
    setGlobalProperties({
      ...globalProperties,
      inviteAuthorisedModal: {
        ...globalProperties.inviteAuthorisedModal,
        showInviteAuthorisedModal: true,
      }
    })
  }

  const handleExpiredChange = () => {
    const newShowExpired = !showExpired;
    setShowExpired(newShowExpired);
    setPending({
      ...pendingInvitations,
      page: 1,
      ...(newShowExpired ? { status: 'EXPIRED' } : { status: undefined }),
    });
  };

  const handleChangeAllCheckbox = () => {
    const allIdsArray = data?.map(invitation => invitation)

    if (invitationsCheckboxIds.length === 0) {
      setInvitationsCheckboxIds(allIdsArray)
      setSelectAllActive(true)
    } else if (allInvitationsSelected) {
      setInvitationsCheckboxIds([])
      setSelectAllActive(false)
    } else {
      setInvitationsCheckboxIds(allIdsArray)
      setSelectAllActive(true)
    }
  }

  const handleIndividualCheckbox = (invitation, key) => {
    if (invitationsCheckboxIds.some(checbkoxInvitationArr => checbkoxInvitationArr.invitationID === invitation.invitationID)) {
     
      const updatedInvitations = [...invitationsCheckboxIds]

      const indexToRemove = updatedInvitations.map((checkboxInvitationsArr) => {
          return checkboxInvitationsArr.invitationID
      }).indexOf(invitation.invitationID);
      
      updatedInvitations.splice(indexToRemove, 1);
      setInvitationsCheckboxIds(updatedInvitations)
    } else {
      setInvitationsCheckboxIds((prevIds) => [...prevIds, invitation]);
    }
  }
  
  return (
    <div className={classes.modalContent} onScroll={handleScroll}>
      <div className={classes.dividerContainer}>
        <hr className={classes.border}/>
      </div>
      { shellUserCount > 0 && providerCode === AIRLINES.AA && checkPermission("F_INVITE_USER") &&
        <AuthorisedCardUsersNotice 
          title={<FormattedMessage id="authorisedCardUserNotice.title" values={{users: globalProperties?.inviteAuthorisedModal?.shellUserCount}}/>}
          description={<FormattedMessage id="authorisedCardUserNotice.description"/>}
          button={<FormattedMessage id="authorisedCardUserNotice.button"/>}
          handleButton={handleInviteNow}
        />
      }
      {pendingInviteResponse?.results?.dataItems ?
        <div className={classes.pendingContent}>
          <div className={classes.actionContainer}>
            <UCInput
              className={classes.search}
              icon={ <SearchIcon /> }
              placeholder={intl.formatMessage(messages.searchAreaMessage)}
              onChange={event => setQuery(event.target.value)}
            />
            <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderSort()} onEnter={() => setOverlayOpen(true)} onExit={() => setOverlayOpen(false)}>
              <div className={`${classes.sortIcon} ${overlayOpen ? classes.dotsOpenStyle : ""}`}>
                <SortMenuIcon/>
              </div>
            </OverlayTrigger>
          </div>
          <div className={classes.showExpiredContainer}>
            <div className={`${classes.allCheckboxDiv} ${showExpired ? classes.fullySelected : ""}`}>
              <input
                onChange={ () => handleExpiredChange() }
                type="checkbox"
                name={ "checkAll" }
                id={ "checkAll" }
                checked={ showExpired }
              />
            </div>
            <FormattedMessage id="people.pendingInvites.expiredInvitations"/>
          </div>
          <div className={classes.gridHeader}>
            <div className={classes.headerColumns}>
              <div className={classes.emailText}>
                {checkPermission("F_INVITE_USER") && (
                  <div className={`${classes.allCheckboxDiv} ${allInvitationsSelected ? classes.fullySelected : ""} ${invitationsCheckboxIds.length > 0 ? classes.partiallySelected : ""}`}>
                    <input
                      onChange={ () => handleChangeAllCheckbox() }
                      type="checkbox"
                      name={ "checkAll" }
                      id={ "checkAll" }
                      checked={ (data.length !== 0 && query.length === 0 && allInvitationsSelected ) || invitationsCheckboxIds.length > 0 }
                    />
                  </div>
                )}
                <FormattedMessage id="pendinginvitations.emailColumn" />
              </div>
              <div className={classes.roleText}>
                <FormattedMessage id="pendinginvitations.roleColumn" />
              </div>
            </div>
          </div>
          {
            pendingInviteResponse?.results?.dataItems.length === 0 ?
                <div className={classes.noInviteText}>
                  <FormattedMessage id="pendinginvitations.noResults" />
                </div>
               :
              <>
                {
                  data.map((invitation, key) => {
                    return (
                      <div className={classes.rowGeneralDiv} key={key}>
                        <div className={classes.emailDiv}>
                          {checkPermission("F_INVITE_USER") && (
                            <div className={`${classes.singleCheckboxDiv} ${invitationsCheckboxIds.find(checkboxArrInvitation => checkboxArrInvitation.invitationID === invitation.invitationID) ? classes.singleSelected : ""}`}>
                              <input
                                onChange={ () => handleIndividualCheckbox(invitation, key) }
                                type="checkbox"
                                name={ "individualCheckbox" }
                                id={ "individualCheckbox" }
                                checked={ invitationsCheckboxIds.find(checkboxArrInvitation => checkboxArrInvitation.invitationID === invitation.invitationID) }
                              />
                            </div>
                          )}
                          <div className={classes.mailContainer}>
                            <div className={classes.firstRow}>
                              <div className={classes.email}>{invitation.email}</div>
                              {invitation.expired && <div className={classes.expiredPill}><FormattedMessage id="pendinginvitations.expired"/></div>}
                            </div>
                            <div className={classes.emailDeteils}>
                              {isMobile ? (
                                <>
                                  <FormattedMessage id="pendinginvitations.by" />{" "}{invitation.creatorFirstName}{" "}{invitation.creatorLastName}
                                  <p className={classes.travDetails}>{formatTimeInterval(invitation.invitationDateTime, "pendinginvitations")}</p>
                                </>
                              ) : (
                                <>
                                  <FormattedMessage id="pendinginvitations.by" />{" "}{invitation.creatorFirstName} {invitation.creatorLastName} • {formatTimeInterval(invitation.invitationDateTime, "pendinginvitations")}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={classes.roleActionDiv}>
                          <div className={classes.roleTextRow}>
                            <FormattedMessage id={`roles.${invitation.roleId}.${providerCode}`} />
                          </div>
                          <div className={classes.actionButtonContainer}>
                            {
                              checkPermission("F_INVITE_USER") &&
                                <>
                                  {
                                    <UCButton
                                      onClick={(e) => {
                                        handleResend(e, invitation);
                                      }}
                                      className={classes.sendBtn}
                                      text={<SendIcon/>} 
                                      loadingStateStyling={classes.resendBtnLoadingState}
                                      spinner={{
                                        name: `resendInviteSpinner.${invitation.invitationID}`,
                                        size: 20,
                                      }}
                                    />
                                  }
                                </>
                            }
                            {
                              checkPermission("F_DELETE_INVITE") &&
                                <>
                                  {
                                    <UCButton
                                      onClick={(e) => handleDelete(e, invitation)}
                                      className={classes.deleteBtn}
                                      text={<DeleteIcon/>}
                                      loadingStateStyling={classes.revokeLoadingState}
                                      spinner={{
                                        name: `revokeInviteSpinner.${invitation.invitationID}`,
                                        size: 20,
                                      }}
                                    />
                                  }
                                </>
                            }
                          </div>
                        </div>
                      </div>
                    )
                  })
                }
                {invitationsCheckboxIds.length !== 0 &&
                  <div className={classes.spacerDiv} />
                }
                {invitationsCheckboxIds.length !== 0 &&
                  <div className={classes.multipleResendBar}>
                    <div className={classes.numberOfInvites}>
                      <div className={classes.icon}>
                        <MailIcon />
                      </div>
                      <div className={classes.totalInvitesText}>
                        {isFetching ? (
                          <Spinner size={20} color={theme.spinners["default"]} customClass={classes.invitesSpinner}/>
                        ) : <FormattedMessage id="pendinginvitations.invites.selected" values={{ selectedInvites: selectAllActive && query.length <= 2 ? (filterTotal ?? invitationsCheckboxIds.length) : invitationsCheckboxIds.length}}/> } 
                      </div>
                    </div>
                    <div className={classes.multipleResendButtons}>
                      <UCButton
                        type="default"
                        className={classes.cancelButton}
                        text={<FormattedMessage id="pendinginvitations.cancel" />}
                        onClick={() => handleCancelResend()}
                      />

                      <UCButton
                        type="primary"
                        className={classes.resendButton}
                        text={<FormattedMessage id="pendinginvitations.resend" />}
                        onClick={() => handleMultipleResend()}
                        loadingStateStyling={classes.customLoading}
                        spinner={{
                          name: "resendInviteSpinner.multiple",
                          size: 20
                        }}
                      />
                    </div>
                  </div>
                }
              </>
          }
        </div>
        :
        <div>
          <div className={classes.noInvitationsTitle}>
            <FormattedMessage id="pendinginvitations.noPending" />
          </div>
          <div className={classes.noInvieText}>
            <FormattedMessage id="pendinginvitations.noPendingMembers" />
          </div>
        </div>
      }
    </div>
  )
}

export default Content;